function loadSuruLeaflet() {
  if (window.L?.map) return Promise.resolve(window.L);
  return Promise.reject(new Error('The theme-hosted Leaflet library did not load.'));
}

function escapeSuruText(value = '') {
  return String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character]);
}

if (!customElements.get('suru-terrain-map')) {
  class SuruTerrainMap extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('[data-leaflet-canvas]');
      this.points = Array.from(this.querySelectorAll('[data-map-point]'));
      this.fallback = this.querySelector('[data-map-fallback]');
      if (!this.canvas) return;

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.initialize();
        }, { rootMargin: '300px' });
        this.observer.observe(this);
      } else {
        this.initialize();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.map?.remove();
    }

    async initialize() {
      if (this.map || this.loading) return;
      this.loading = true;

      try {
        const L = await loadSuruLeaflet();
        this.map = L.map(this.canvas, {
          scrollWheelZoom: false,
          zoomControl: true,
          preferCanvas: true,
        });

        const tileUrl = this.dataset.tileUrl || 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png';
        const attribution = 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>, SRTM | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)';
        let tileErrors = 0;
        L.tileLayer(tileUrl, {
          attribution,
          maxZoom: 17,
          maxNativeZoom: 17,
          updateWhenIdle: true,
          keepBuffer: 2,
        }).on('tileerror', () => {
          tileErrors += 1;
          if (tileErrors >= 4) this.showFallback('The terrain tiles could not load. Use the location list below.');
        }).addTo(this.map);

        const terrainLabel = L.control({ position: 'topright' });
        terrainLabel.onAdd = () => {
          const label = L.DomUtil.create('div', 'suru-leaflet-layer-label');
          label.textContent = 'Topographic terrain';
          return label;
        };
        terrainLabel.addTo(this.map);

        const validPoints = this.points.filter((point) => this.isValidPoint(point));
        validPoints.forEach((point, index) => this.addPoint(L, point, index));

        const centerLatitude = Number(this.dataset.centerLat);
        const centerLongitude = Number(this.dataset.centerLng);
        const defaultZoom = Number(this.dataset.zoom) || (this.dataset.mapKind === 'problems' ? 16 : 13);

        if (validPoints.length > 1) {
          this.map.fitBounds(validPoints.map((point) => [Number(point.dataset.lat), Number(point.dataset.lng)]), {
            padding: [48, 48],
            maxZoom: Number(this.dataset.maxZoom) || defaultZoom,
          });
        } else if (validPoints.length === 1) {
          this.map.setView([Number(validPoints[0].dataset.lat), Number(validPoints[0].dataset.lng)], defaultZoom);
        } else if (Number.isFinite(centerLatitude) && Number.isFinite(centerLongitude)) {
          this.map.setView([centerLatitude, centerLongitude], defaultZoom);
        } else {
          this.showFallback('Verified map coordinates have not been added yet. Use the location list below.');
          this.map.setView([34.32, 75.97], 12);
        }

        this.classList.add('is-ready');
        this.fallback?.setAttribute('hidden', '');
        requestAnimationFrame(() => this.map.invalidateSize());
      } catch (error) {
        this.showFallback('The interactive terrain map could not load. Use the location list below.');
      } finally {
        this.loading = false;
      }
    }

    isValidPoint(point) {
      return Number.isFinite(Number(point.dataset.lat)) && Number.isFinite(Number(point.dataset.lng));
    }

    addPoint(L, point, index) {
      const isReference = point.dataset.mapRole === 'sector-reference';
      const markerNumber = point.dataset.number || String(index + 1);
      const markerLabel = point.dataset.name || 'Mapped point';
      const statusClass = (point.dataset.status || 'normal').toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const icon = L.divIcon({
        className: 'suru-leaflet-pin-wrap',
        html: `<span class="suru-leaflet-pin suru-leaflet-pin--${escapeSuruText(isReference ? 'reference' : statusClass)}"><span>${escapeSuruText(markerNumber)}</span><strong>${escapeSuruText(markerLabel)}</strong></span>`,
        iconSize: [42, 42],
        iconAnchor: [21, 21],
      });

      const marker = L.marker([Number(point.dataset.lat), Number(point.dataset.lng)], {
        icon,
        keyboard: true,
        title: markerLabel,
        alt: `${markerLabel} map marker`,
        riseOnHover: true,
      }).addTo(this.map);

      point._suruLeafletMarker = marker;
      point._suruLeafletMap = this.map;

      if (this.dataset.mapKind === 'sectors') {
        marker.on('click', () => {
          this.closest('suru-guide')?.showMarker(point);
        });
        marker.on('mouseover', () => {
          if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
            this.closest('suru-guide')?.showMarker(point, { emit: false });
          }
        });
      } else if (!isReference) {
        marker.bindPopup(this.buildProblemPopup(point), { maxWidth: 320, minWidth: 210 });
        marker.on('click', () => this.emitInteraction(point.dataset.mapRole === 'boulder' ? 'boulder_map_marker_click' : 'problem_map_marker_click', {
          sector: point.dataset.sector || '',
          item: point.dataset.handle || '',
        }));
      } else {
        marker.bindTooltip('Sector reference coordinate');
      }
    }

    buildProblemPopup(point) {
      const popup = document.createElement('article');
      popup.className = 'suru-problem-popup';
      const heading = document.createElement('h3');
      heading.textContent = point.dataset.name || 'Climbing problem';
      popup.appendChild(heading);

      const facts = [point.dataset.grade, point.dataset.boulder, point.dataset.problems, point.dataset.page].filter(Boolean);
      if (facts.length) {
        const meta = document.createElement('p');
        meta.className = 'suru-problem-popup__meta';
        meta.textContent = facts.join(' · ');
        popup.appendChild(meta);
      }

      if (point.dataset.description) {
        const description = document.createElement('p');
        description.textContent = point.dataset.description;
        popup.appendChild(description);
      }
      return popup;
    }

    setPointVisible(point, visible) {
      const marker = point._suruLeafletMarker;
      if (!marker || !this.map) return;
      if (visible && !this.map.hasLayer(marker)) marker.addTo(this.map);
      if (!visible && this.map.hasLayer(marker)) marker.removeFrom(this.map);
    }

    showFallback(message) {
      if (!this.fallback) return;
      this.fallback.textContent = message;
      this.fallback.hidden = false;
    }

    emitInteraction(name, detail = {}) {
      document.dispatchEvent(new CustomEvent('suru:interaction', { detail: { name, ...detail } }));
    }
  }

  customElements.define('suru-terrain-map', SuruTerrainMap);
}

if (!customElements.get('suru-guide')) {
  class SuruGuide extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-suru-sector-card]'));
      this.markers = Array.from(this.querySelectorAll('[data-suru-marker]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-suru-filter]'));
      this.filterStatus = this.querySelector('[data-suru-filter-status]');
      this.mapCard = this.querySelector('[data-suru-map-card]');
      this.terrainMap = this.querySelector('suru-terrain-map');
      this.activeMarker = null;

      this.bindMap();
      this.bindFilters();
      this.bindAnalyticsHooks();
    }

    bindMap() {
      this.querySelector('[data-suru-map-close]')?.addEventListener('click', () => {
        this.mapCard.hidden = true;
        this.activeMarker?._suruLeafletMarker?.getElement()?.focus();
        this.activeMarker = null;
      });
    }

    showMarker(marker, options = {}) {
      if (!this.mapCard) return;
      this.activeMarker = marker;
      this.mapCard.querySelector('[data-suru-map-name]').textContent = marker.dataset.name || '';
      this.mapCard.querySelector('[data-suru-map-description]').textContent = marker.dataset.description || '';

      const meta = [
        marker.dataset.grades ? `Grades ${marker.dataset.grades}` : '',
        marker.dataset.problems ? `${marker.dataset.problems} problems` : '',
        marker.dataset.approach ? `Approach ${marker.dataset.approach}` : '',
      ].filter(Boolean);
      this.mapCard.querySelector('[data-suru-map-meta]').textContent = meta.join(' · ');

      const statusHolder = this.mapCard.querySelector('[data-suru-map-status]');
      statusHolder.replaceChildren();
      const badge = document.createElement('span');
      const status = marker.dataset.status || 'Normal';
      badge.className = `suru-status suru-status--${status.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
      badge.textContent = status;
      badge.setAttribute('aria-label', `Conservation status: ${status}`);
      statusHolder.appendChild(badge);

      const image = this.mapCard.querySelector('[data-suru-map-image]');
      if (marker.dataset.image) {
        image.style.backgroundImage = `url("${marker.dataset.image.replace(/"/g, '%22')}")`;
        image.hidden = false;
      } else {
        image.style.backgroundImage = '';
        image.hidden = true;
      }

      const link = this.mapCard.querySelector('[data-suru-map-link]');
      link.href = marker.dataset.url || '#sector-list';
      link.dataset.suruEvent = 'map_sector_open';
      link.dataset.suruSector = marker.dataset.handle || '';
      this.mapCard.hidden = false;
      if (options.emit !== false) this.emitInteraction('map_marker_click', { sector: marker.dataset.handle || '' });
    }

    bindFilters() {
      this.filterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.filterButtons.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          this.applyFilter(button.dataset.suruFilter || 'all');
        });
      });
    }

    applyFilter(filter) {
      const matches = (element) => {
        if (filter === 'all') return true;
        if (filter === 'beginner') return element.dataset.sectorBeginner === 'true';
        return element.dataset.sectorStatus === filter;
      };

      let visibleCount = 0;
      this.cards.forEach((card) => {
        const visible = matches(card);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this.markers.forEach((marker) => {
        const card = this.cards.find((item) => item.dataset.sectorName === marker.dataset.name);
        const equivalent = {
          dataset: {
            sectorBeginner: card?.dataset.sectorBeginner,
            sectorStatus: (marker.dataset.status || '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          },
        };
        this.terrainMap?.setPointVisible(marker, matches(equivalent));
      });

      if (this.mapCard && !this.mapCard.hidden) this.mapCard.hidden = true;
      if (this.filterStatus) this.filterStatus.textContent = `${visibleCount} sector${visibleCount === 1 ? '' : 's'} shown.`;
      this.emitInteraction('filter_use', { filter, resultCount: visibleCount });
    }

    bindAnalyticsHooks() {
      this.addEventListener('click', (event) => {
        const target = event.target.closest('[data-suru-event]');
        if (!target) return;
        this.emitInteraction(target.dataset.suruEvent, {
          sector: target.dataset.suruSector || '',
          destination: target.dataset.suruDestination || '',
        });
      });
    }

    emitInteraction(name, detail = {}) {
      document.dispatchEvent(new CustomEvent('suru:interaction', { detail: { name, ...detail } }));
    }
  }

  customElements.define('suru-guide', SuruGuide);
}

if (!customElements.get('suru-problem-directory')) {
  class SuruProblemDirectory extends HTMLElement {
    connectedCallback() {
      this.boulders = Array.from(this.querySelectorAll('.suru-boulder[data-boulder]'));
      this.search = this.querySelector('[data-directory-search]');
      this.grade = this.querySelector('[data-directory-grade]');
      this.type = this.querySelector('[data-directory-type]');
      this.sort = this.querySelector('[data-directory-sort]');
      this.status = this.querySelector('[data-directory-status]');
      this.empty = this.querySelector('[data-directory-empty]');
      this.list = this.querySelector('[data-directory-list]');

      [this.search, this.grade, this.type, this.sort].forEach((control) => {
        control?.addEventListener(control === this.search ? 'input' : 'change', () => this.apply());
      });
      this.querySelector('[data-directory-reset]')?.addEventListener('click', () => this.reset());
      this.removeUnavailableOptions();
      this.apply({ emit: false });
    }

    removeUnavailableOptions() {
      const problems = this.getProblems();
      const availability = {
        'v0-v3': problems.some((problem) => (problem.dataset.gradeBand || this.gradeBand(problem.dataset.grade)) === 'v0-v3'),
        'v4-v6': problems.some((problem) => (problem.dataset.gradeBand || this.gradeBand(problem.dataset.grade)) === 'v4-v6'),
        'v7-plus': problems.some((problem) => (problem.dataset.gradeBand || this.gradeBand(problem.dataset.grade)) === 'v7-plus'),
        ungraded: problems.some((problem) => (problem.dataset.gradeBand || this.gradeBand(problem.dataset.grade)) === 'ungraded'),
        sit: problems.some((problem) => (problem.dataset.start || '').includes('sit')),
        stand: problems.some((problem) => (problem.dataset.start || '').includes('stand')),
        crack: problems.some((problem) => (problem.dataset.symbols || '').includes('crack')),
        'rough-landing': problems.some((problem) => (problem.dataset.symbols || '').includes('rough-landing')),
        'high-wall': problems.some((problem) => problem.dataset.highball === 'true' || (problem.dataset.symbols || '').includes('high-wall')),
        project: problems.some((problem) => (problem.dataset.symbols || '').includes('project')),
        danger: problems.some((problem) => (problem.dataset.symbols || '').includes('danger')),
        recommended: problems.some((problem) => (problem.dataset.symbols || '').includes('recommended')),
        compression: problems.some((problem) => (problem.dataset.symbols || '').includes('compression')),
        arete: problems.some((problem) => (problem.dataset.symbols || '').includes('arete')),
        traverse: problems.some((problem) => (problem.dataset.symbols || '').includes('traverse')),
        mapped: problems.some((problem) => problem.dataset.mapped === 'true'),
      };

      [this.grade, this.type].forEach((select) => {
        Array.from(select?.options || []).forEach((option) => {
          if (option.value !== 'all' && !availability[option.value]) option.remove();
        });
      });
    }

    getProblems() {
      return this.boulders.flatMap((boulder) => Array.from(boulder.querySelectorAll('[data-problem]')));
    }

    gradeNumber(value = '') {
      const match = String(value).toUpperCase().match(/V\s*(\d+)/);
      return match ? Number(match[1]) : Number.POSITIVE_INFINITY;
    }

    gradeBand(value = '') {
      const grade = this.gradeNumber(value);
      if (!Number.isFinite(grade)) return 'ungraded';
      if (grade <= 3) return 'v0-v3';
      if (grade <= 6) return 'v4-v6';
      return 'v7-plus';
    }

    problemMatches(problem, boulderMatchesSearch) {
      const query = (this.search?.value || '').trim().toLowerCase();
      const searchText = `${problem.dataset.name || ''} ${problem.dataset.boulder || ''}`.toLowerCase();
      const selectedGrade = String(this.querySelector('[data-directory-grade]')?.value || 'all').trim();
      const problemGradeBand = String(problem.getAttribute('data-grade-band') || this.gradeBand(problem.getAttribute('data-grade') || '')).trim();
      const selectedType = String(this.querySelector('[data-directory-type]')?.value || 'all').trim();
      const startType = problem.getAttribute('data-start') || '';
      const symbols = problem.getAttribute('data-symbols') || '';
      let matches = true;

      if (query && !boulderMatchesSearch && !searchText.includes(query)) matches = false;
      if (selectedGrade !== 'all' && problemGradeBand !== selectedGrade) matches = false;
      if (selectedType === 'sit' && !startType.includes('sit')) matches = false;
      if (selectedType === 'stand' && !startType.includes('stand')) matches = false;
      if (selectedType === 'mapped' && problem.getAttribute('data-mapped') !== 'true') matches = false;
      if (!['all', 'sit', 'stand', 'mapped'].includes(selectedType) && !symbols.includes(selectedType)) matches = false;
      return matches;
    }

    apply(options = {}) {
      const query = (this.search?.value || '').trim().toLowerCase();
      let visibleProblems = 0;
      let visibleBoulders = 0;

      this.boulders.forEach((boulder) => {
        const boulderMatchesSearch = Boolean(query && (boulder.dataset.name || '').toLowerCase().includes(query));
        const problems = Array.from(boulder.querySelectorAll('[data-problem]'));
        let matchesInBoulder = 0;
        problems.forEach((problem) => {
          const visible = this.problemMatches(problem, boulderMatchesSearch);
          problem.hidden = !visible;
          if (visible) matchesInBoulder += 1;
        });

        const hasActiveProblemFilter = (this.grade?.value !== 'all') || (this.type?.value !== 'all');
        const visible = problems.length
          ? matchesInBoulder > 0
          : (!hasActiveProblemFilter && (!query || boulderMatchesSearch));
        boulder.hidden = !visible;
        if (visible) visibleBoulders += 1;
        visibleProblems += matchesInBoulder;
      });

      this.applySort();
      if (this.empty) this.empty.hidden = visibleBoulders > 0;
      if (this.status) this.status.textContent = `${visibleBoulders} boulder${visibleBoulders === 1 ? '' : 's'} · ${visibleProblems} problem${visibleProblems === 1 ? '' : 's'} shown`;
      if (options.emit !== false) this.emitInteraction('problem_directory_filter', {
        sector: this.dataset.sector || '',
        grade: this.grade?.value || 'all',
        type: this.type?.value || 'all',
        sort: this.sort?.value || 'guide',
        resultCount: visibleProblems,
      });
    }

    applySort() {
      const sort = this.sort?.value || 'guide';
      const compareText = (a, b, key) => (a.dataset[key] || '').localeCompare(b.dataset[key] || '', undefined, { numeric: true });
      const compareNumber = (a, b, key) => Number(a.dataset[key] || 9999) - Number(b.dataset[key] || 9999);
      const compareGrade = (a, b, direction) => {
        const first = this.gradeNumber(a.dataset.grade);
        const second = this.gradeNumber(b.dataset.grade);
        if (!Number.isFinite(first) && !Number.isFinite(second)) return 0;
        if (!Number.isFinite(first)) return 1;
        if (!Number.isFinite(second)) return -1;
        return (first - second) * direction;
      };

      const orderedBoulders = [...this.boulders].sort((a, b) => {
        if (sort === 'boulder') return compareText(a, b, 'name');
        return compareNumber(a, b, 'order');
      });
      orderedBoulders.forEach((boulder) => this.list?.appendChild(boulder));

      this.boulders.forEach((boulder) => {
        const problemList = boulder.querySelector('.suru-problem-list');
        const rows = Array.from(boulder.querySelectorAll('[data-problem]'));
        rows.sort((a, b) => {
          if (sort === 'problem') return compareText(a, b, 'name');
          if (sort === 'grade-asc') return compareGrade(a, b, 1);
          if (sort === 'grade-desc') return compareGrade(a, b, -1);
          return compareNumber(a, b, 'order');
        });
        rows.forEach((row) => problemList?.appendChild(row));
      });
    }

    reset() {
      if (this.search) this.search.value = '';
      if (this.grade) this.grade.value = 'all';
      if (this.type) this.type.value = 'all';
      if (this.sort) this.sort.value = 'guide';
      this.apply();
      this.search?.focus();
    }

    emitInteraction(name, detail = {}) {
      document.dispatchEvent(new CustomEvent('suru:interaction', { detail: { name, ...detail } }));
    }
  }

  customElements.define('suru-problem-directory', SuruProblemDirectory);
}
